/**
 * Turning a failed API answer into words for people: messages the server wrote for people are shown as they are;
 * no answer, a server error without a code, and a bare permission or sign-in refusal get friendly
 * text instead of whatever came back. Branches on status and code, never on the message.
 */
import { NETWORK_ERROR_TEXT, fallbackErrorText, type ApiResponse } from './api-contract';

/**
 * Anything with an HTTP status and maybe a code and message: fetchApi's ApiResponse, or a result
 * that keeps the code in `body`.
 */
export interface ApiFailure {
  status: number;
  code?: string;
  error?: string | null;
  body?: Record<string, unknown>;
}

export const OFFLINE_ERROR_TEXT = "You're offline. Check your connection, then try again.";

/** The error's code (the server's `code`), when it has one. */
export function codeOf(res: ApiFailure): string | undefined {
  if (res.code) return res.code;
  return typeof res.body?.code === 'string' ? res.body.code : undefined;
}

function browserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * The sentence to show for a failed request: in a toast for a failed action, under LoadError's
 * title for a failed load. `fallback` is used when nothing better is known.
 */
export function describeApiError(res: ApiFailure, fallback?: string): string {
  const code = codeOf(res);
  const serverText = res.error?.trim() || undefined;

  // No answer at all.
  if (res.status === 0) {
    if (code === 'ABORTED') return 'The request was cancelled.';
    if (code === 'TIMEOUT') return 'Software Factory took too long to answer. Try again.';
    return browserOffline() ? OFFLINE_ERROR_TEXT : NETWORK_ERROR_TEXT;
  }
  // Something in front of the Worker answered (a Cloudflare page), or the body wasn't JSON.
  if (code === 'BAD_RESPONSE') return fallbackErrorText(res.status);

  if (res.status >= 500) {
    // A coded 5xx was written for people (DATABASE_UNAVAILABLE, for one); an
    // uncoded one, from routes not yet moved to the error helpers, may be raw exception text.
    return code && serverText ? serverText : fallbackErrorText(res.status);
  }
  if (res.status === 401 || res.status === 403) {
    // "Unauthorized - Invalid token" and "Forbidden - Insufficient permissions" from older routes
    // carry no code; the coded ones ("Your role doesn't allow this.") are written for people.
    return code && serverText ? serverText : fallbackErrorText(res.status);
  }
  return serverText ?? fallback ?? fallbackErrorText(res.status);
}

/** A failed ApiResponse as an exception, for code that wants to throw (see `unwrap`). */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fields?: Record<string, string>;

  constructor(res: ApiFailure & { fields?: Record<string, string> }) {
    super(describeApiError(res));
    this.name = 'ApiError';
    this.status = res.status;
    this.code = codeOf(res);
    this.fields = res.fields;
  }
}

/**
 * A request its own caller cancelled (a superseded or unmounted load): not a failure, so never
 * shown or logged as one. Takes an ApiResponse or an ApiError.
 */
export function isAborted(res: ApiFailure): boolean {
  return res.status === 0 && codeOf(res) === 'ABORTED';
}

/** The data of a successful answer; throws ApiError for a failed one. */
export function unwrap<T>(res: ApiResponse<T>): T {
  if (res.error !== undefined || res.status < 200 || res.status >= 300) throw new ApiError(res);
  return res.data as T;
}
